import {
  OperationVariables,
  QueryResultWrapper,
  useLazyQuery,
  useMutation,
  useQuery,
  useSubscription,
} from '@apollo/client'
import {Grid2, IconButton, LinearProgress, Snackbar} from '@mui/material'
import {makeStyles} from '@mui/styles'
import gql from 'graphql-tag'
import {orderBy, unionBy} from 'lodash-es'
import {
  FC,
  MutableRefObject,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import {
  useGetIdentity,
  useGetList,
  useNotify,
  useTranslate,
} from 'react-admin'
import {usePageVisibility} from 'react-page-visibility'

import useFileSelection from '../../hooks/useFileSelection'
import useSessionMerchantUser from '../../hooks/useSessionMerchantUser'
import useTracker from '../../hooks/useTracker'
import {
  ChatMessageFileAttachment,
  ChatMessages,
  ChatTypesEnum,
  MerchantUsers,
  MutationRoot,
  MutationRootGeneratePresignedUrlsArgs,
  MutationRootInsertChatMessageArgs,
  QueryRoot,
  WhatsappMessageStatusesEnum,
  WhatsappMessageTypesEnum,
} from '../../types/graphqlSchema'
import {isEmbedded} from '../../utils/embedded'
import {isNativeMobileApp, platform} from '../../utils/platform'
import uuidv4 from '../../utils/uuid'
import {ChatMessageTemplateToolbarIcon} from '../icons'
import MobileFilePickerPopper from '../MobileFilePickerPopper'
import ChatAutoTranslationSettingButton from './ChatAutoTranslationSettingButton'
import ChatBulkActionsToolbar from './ChatBulkActionsToolbar'
import {ChatContext} from './ChatContext'
import ChatMessageList, {ChatMessageListForwardRef} from './ChatMessageList'
import ChatToolbar from './ChatToolbar'

const Chat: FC<ChatProps> = ({
  header,
  // TODO: reply windows is specific to external chats and should not leak here
  isReplyWindowExpired,
  // TODO: translation settings are only for external chats and should not leak here
  isShowingTranslations,
  onChangeIsShowingTranslations,
  // TODO: ExternalInboundChatMessageReceived is specific to external chats and should not
  //  leak here
  onExternalInboundChatMessageReceived,
  // TODO: chat type specific behavior should not leak here
  type,
}) => {
  const styles = useStyles()
  const notify = useNotify()
  const {trackEvent} = useTracker()
  const isPageVisible = usePageVisibility() ?? true
  const [text, setText] = useState("")
  const [isTemplateDialogOpen, setIsTemplateDialogOpen] = useState(false)
  const [isMobileFilePickerPopperOpen, setIsMobileFilePickerPopperOpen] = useState(false)
  const {identity: {id: userId, isImpersonatedUser} = {}} = useGetIdentity()
  const {merchantUser} = useSessionMerchantUser()
  const chatMessageListContainerRef = useRef<HTMLDivElement>()
  const firstLoadEffectRef = useRef(true)
  const scrollToMostRecentChatMessage = useCallback(() => {
    chatMessageListRef.current?.scrollToBottom()
  }, [])
  const {
    channelId,
    chatId,
    chatMessageId,
    currentlyRepliedChatMessage,
    setCurrentlyRepliedChatMessage,
    visibleToolbar,
  } = useContext(ChatContext)
  const chatMessagesQueryVariables = {
    chatId,
    cursorComparison: {'_lte': 'NOW()'},
    limit: CHAT_MESSAGES_LOAD_MORE_LIMIT,
    timestampOrderBy: 'desc',
  }
  const userChatQueryVariables = {chatId}
  const chatMessageListRef = useRef<ChatMessageListForwardRef>()
  const [
    lastChatMessageVisibleInTheViewPort, setLastChatMessageVisibleInTheViewPort,
  ] = useState<ChatMessages>()
  const {
    data: {user_chats: [userChat] = []} = {},
  } = useQuery<QueryRoot['user_chats']>(USER_CHAT_QUERY, {
    skip: !chatId, variables: userChatQueryVariables,
  })
  const mostRecentReadChatMessage = userChat?.mostRecentReadChatMessage
  const {
    data: {chat_messages: chatMessages} = {},
    fetchMore,
    loading: isLoadingChatMessages,
    refetch: refetchChatMessages,
    updateQuery,
  } = useQuery<QueryRoot['chat_messages']>(
    CHAT_MESSAGES_QUERY, {variables: chatMessagesQueryVariables},
  )
  useEffect(() => {
    // Let's re-order messages by "chatMessageTimestamp" every time the
    // chatMessages change. This is to avoid the weird issue were we see message bubbles
    // jumping on the screen because there's an ordering disorder/conflict between the
    // "insertionTimestamp" and the "chatMessageTimestamp".
    // We also don't want to order chat messages by chatMessageTimestamp directly from
    // the apollo because of the performance cost it brings.
    updateQuery(prev => ({
      ...prev,
      chat_messages: orderBy(
        unionBy(chatMessages as ChatMessages[], prev?.chat_messages, 'id'),
        'timestamp',
        'desc',
      ),
    }))
  }, [chatMessages, updateQuery])
  useQuery<QueryRoot['chat_messages_by_pk']>(
    SELECTED_CHAT_MESSAGE_QUERY,
    {
      async onCompleted({
        chat_messages_by_pk: {id, insertionTimestamp = undefined} = {} as ChatMessages,
      }) {
        if (!insertionTimestamp) return
        if (chatMessages?.some(m => m.id === id)) {
          return chatMessageListRef.current?.scrollToChatMessage(
            id, {behavior: 'smooth', delay: 0}
          )
        }
        const {data: {chat_messages: beforeChatMessages}} = await fetchMore({
          updateQuery: (
            {chat_messages: previousChatMessages = []} = {} as any,
          ) => ({
            chat_messages: orderBy(
              // Keep the same messages as before not to update the messages list
              // twice. The final messages list will be given at once in the
              // "after message" load. This also helps to prevent the scroll bar from
              // weirdly jumping before going to the selected message.
              previousChatMessages,
              'timestamp',
              'desc'
            ),
          } as QueryResultWrapper<QueryRoot['chat_messages']>),
          variables: {
            cursorComparison: {'_lte': insertionTimestamp},
            limit: 15,
          },
        })
        await fetchMore({
          updateQuery: (
            __,
            {fetchMoreResult: {chat_messages: afterChatMessages} = {}},
          ) => ({
            chat_messages: orderBy(
              unionBy(beforeChatMessages, afterChatMessages, 'id'),
              'timestamp',
              'desc'
            ),
          } as QueryResultWrapper<QueryRoot['chat_messages']>),
          variables: {
            cursorComparison: {'_gte': insertionTimestamp},
            limit: 15,
            timestampOrderBy: 'asc',
          },
        })
        chatMessageListRef.current?.scrollToChatMessage(id, {delay: 0})
      },
      skip: !(
        chatMessageId &&
        // There's still a small chance that this query is completed before the
        // "chat messages query".
        // The "chat Messages query" must have been called at least once to run this
        // "selected chat message" query. If it's not the case, the "fetch more" function
        // called once this query is completed will fail.
        chatMessages
      ),
      variables: {chatMessageId},
    }
  )
  const {data: mimeTypes = []} = useGetList(
    'file_mime_types',
    {sort: {field: 'mimeType', order: 'DESC'}},
  )
  const {
    dropZoneProps,
    files: fileAttachments,
    input: fileAttachmentInput,
    isDraggedFileRejected,
    isDragging,
    openFileSelectionDialog,
    setFiles: setFileAttachments,
    validator: validateFile,
  } = useFileSelection({
    mimeTypes: mimeTypes.map(r => r.mimeType),
    multiple: true,
    shouldCheckForWhatsappBusinessApiMaxFileSize: type === ChatTypesEnum.External,
  })
  const [isUploadingFiles, setIsUploadingFiles] = useState(false)
  const [
    insertChatMessage, {error: chatMessageInsertError, loading: isInsertingChatMessage},
  ] = useMutation<MutationRoot['insertChatMessage'], MutationRootInsertChatMessageArgs>(
    CHAT_MESSAGE_INSERT_MUTATION,
    {
      onCompleted: () => {
        // TODO: Shouldn't this already happening whenever chat messages add to the list?
        scrollToMostRecentChatMessage()
      },
      // @ts-ignore
      update: (cache, {data: {insertChatMessage: {chatMessage}} = {}} = {}) => {
        const {chat_messages: chatMessages}: any = cache.readQuery({
          query: CHAT_MESSAGES_QUERY,
          variables: chatMessagesQueryVariables,
        })
        cache.writeQuery({
          data: {chat_messages: chatMessages.find(c => c.id === chatMessage.id) ?
            chatMessages : [chatMessage, ...chatMessages],
          },
          query: CHAT_MESSAGES_QUERY,
          variables: chatMessagesQueryVariables,
        })
      },
      variables: {
        platform: isEmbedded ? 'iframe' : platform,
        repliedChatMessageId: currentlyRepliedChatMessage?.id,
      } as MutationRootInsertChatMessageArgs,
    },
  )
  const [generatePresignedUrls] = useMutation<
    MutationRoot['generatePresignedUrls'],
    MutationRootGeneratePresignedUrlsArgs
  >(GENERATE_PRESIGNED_URLS_MUTATION)
  const sendChatMessage = useCallback(
    async options => {
      await insertChatMessage(options)
      setText("")
      setFileAttachments([])
      setIsTemplateDialogOpen(false)
      setCurrentlyRepliedChatMessage(undefined)
    },
    [insertChatMessage, setFileAttachments, setCurrentlyRepliedChatMessage],
  )
  const [upsertMostRecentReadChatMessageId] = useMutation<
    MutationRoot['insert_user_chats_one']
  >(
    USER_CHAT_UPSERT_MUTATION, {
      // @ts-ignore: Property "insert_user_chats_one" does not exist on type
      // MutationResultWrapper<UserChats | undefined> | {} | null
      update: (cache, {data: {insert_user_chats_one: userChat} = {}} = {}) => {
        cache.writeQuery(
          {data: {user_chats: [userChat]},
            query: USER_CHAT_QUERY,
            variables: userChatQueryVariables,
          })
      },
    })
  const fetchMoreChatMessages = useCallback(async (
    {shouldFetchEarlierMessages}: {shouldFetchEarlierMessages: boolean}
  ) => {
    if (!chatMessages?.length) return
    try {
      await fetchMore({
        updateQuery: (
          {chat_messages: previousChatMessages = []} = {} as any,
          {fetchMoreResult: {chat_messages: moreChatMessages} = {}},
        ) => ({
          chat_messages: orderBy(
            unionBy(previousChatMessages, moreChatMessages, 'id'),
            'timestamp',
            'desc'
          ),
        } as QueryResultWrapper<QueryRoot['chat_messages']>),
        variables: {
          ...(shouldFetchEarlierMessages ? {
            cursorComparison: {
              '_lte': chatMessages[chatMessages.length - 1].insertionTimestamp,
            },
            timestampOrderBy: 'desc',
          } : {
            cursorComparison: {'_gte': chatMessages[0].insertionTimestamp},
            timestampOrderBy: 'asc',
          }) as OperationVariables,
          chatId,
          limit: CHAT_MESSAGES_LOAD_MORE_LIMIT,
        },
      })
    }
    catch (error) {
      // Invariant violation: 17 is thrown when component is unmounted while query is
      // still in progress, e.g. when user navigates to different channel/page.
      // See: https://github.com/apollographql/apollo-client/issues/4114#issuecomment-502111099
      if ((error as Error).toString().includes('Invariant Violation')) {
        return
      }
      throw error
    }
  }, [chatId, chatMessages, fetchMore])
  const sendTemplateChatMessage = useCallback<OnSendTemplateChatMessage>(async ({
    chatMessageTemplateId,
    chatMessageTemplateValues,
    templateHeaderMediaFile,
  }) => {
    const id = uuidv4()
    await sendChatMessage({
      optimisticResponse: optimisticChatMessage({
        channelId,
        chatId,
        id,
        merchantUser,
        text: null,
        type: WhatsappMessageTypesEnum.Template,
      }),
      variables: {
        chat: {chatId},
        ...templateHeaderMediaFile,
        chatMessageTemplateId,
        chatMessageTemplateValues,
        id,
        type: 'TEMPLATE',
      },
    })
    trackEvent({
      action: 'SEND_CHAT_TEMPLATE_MESSAGE',
      category: 'CHAT',
      source: 'CHAT_TEMPLATE_MESSAGE_COMPOSER',
    })
  }, [channelId, chatId, merchantUser, sendChatMessage, trackEvent])
  const sendTextChatMessage = useCallback(async () => {
    const id = uuidv4()
    let chatMessageFileAttachments: ChatMessageFileAttachment[] = []
    if (fileAttachments.length > 0) {
      setIsUploadingFiles(true)
      try {
        const {data} = await generatePresignedUrls({
          variables: {count: fileAttachments.length},
        })
        const presignedUploads = data?.generatePresignedUrls || []
        await Promise.all(fileAttachments.map(async (fileAttachment, index) => {
          const presignedUpload = presignedUploads[index]
          const formData = new FormData()
          Object.entries<string>(presignedUpload.fields).forEach(
            ([key, value]) => formData.set(key, value)
          )
          // S3 requires that the file parameter be added last to the form.
          formData.append('file', fileAttachment.file)
          const uploadResponse = await fetch(presignedUpload.url, {
            body: formData,
            method: 'POST',
          })
          if (!uploadResponse.ok) {
            throw new Error(
              `Failed to upload file ${fileAttachment.filename}: ` +
              `${uploadResponse.statusText}`
            )
          }
        }))
        chatMessageFileAttachments = fileAttachments.map((fileAttachment, index) => ({
          filename: fileAttachment.filename,
          key: presignedUploads[index].key,
          mimeType: fileAttachment.mimeType,
        }))
      }
      catch (error) {
        console.error('File upload failed:', error)
        notify('ra.message.file_upload_error', {type: 'error'})
        return // Don't send message if file uploads failed
      }
      finally {
        setIsUploadingFiles(false)
      }
    }
    await sendChatMessage({
      optimisticResponse: optimisticChatMessage({
        channelId,
        chatId,
        id,
        merchantUser,
        text,
        type: WhatsappMessageTypesEnum.Text,
      }),
      variables: {
        chat: {chatId},
        chatMessageFileAttachments,
        id,
        text,
        type: 'TEXT',
      },
    })
  }, [
    channelId,
    chatId,
    fileAttachments,
    generatePresignedUrls,
    merchantUser,
    notify,
    sendChatMessage,
    text,
  ])
  const resendTextChatMessage = useCallback((chatMessage: ChatMessages) => {
    const id = uuidv4()
    insertChatMessage({
      // @ts-ignore: optimisticResponse not exists in type
      optimisticResponse: optimisticChatMessage({
        channelId,
        chatId,
        id,
        merchantUser,
        text: chatMessage.text,
        type: WhatsappMessageTypesEnum.Text,
      }),
      variables: {
        chat: {chatId},
        chatMessageFileAttachmentIds:
          chatMessage.chatMessageFileAttachments?.map(a => a.id),
        id,
        text: chatMessage.text,
        // @ts-ignore: type doesn't exist
        type: 'TEXT',
      },
    })
  }, [channelId, chatId, insertChatMessage, merchantUser])
  /**
   * We use a lazy query to fetch new chat messages received via the subscription.
   * The subscription itself only returns one field - chatMessageId - which is used
   * to query lazily and update the chat messages cache. This is done because we want
   * the subscription to be as simple as possible (i.e. avoid complex joins in the
   * returned fields or the permissions settings). A complex query leads to slower
   * response times on the subscription as the database CPU becomes bottlenecked.
   */
  const [fetchIncomingMessages] = useLazyQuery<QueryRoot['chat_messages']>(
    INCOMING_CHAT_MESSAGES_QUERY,
    {fetchPolicy: 'network-only'},
  )
  const variables = useMemo(
    () => ({chatId, cursor: new Date().toISOString(), limit: 30}),
    [chatId]
  )
  useSubscription<QueryRoot['chat_messages_event_logs']>(
    CHAT_MESSAGE_EVENT_LOGS_SUBSCRIPTION,
    {
      async onData({data: {data: {chat_messages_event_logs_stream = []} = {}}}) {
        if (!chat_messages_event_logs_stream.length) return
        const chatMessageIds = chat_messages_event_logs_stream.map(c => c.chatMessageId)
        const {
          data: {chat_messages: incomingChatMessages} = {},
        } = await fetchIncomingMessages({variables: {chatMessageIds}})
        updateQuery(prev => {
          // We want to keep the scrollbar at the bottom of the page when a new inbound
          // message is received only if the scrollbar was already at the bottom before
          // the message arrives.
          if (chatMessageListRef.current?.isScrollbarPositionAtBottom()) {
            setTimeout(scrollToMostRecentChatMessage, 10)
          }
          if (incomingChatMessages?.some(m => !!m.authorUser.customerUser)) {
            onExternalInboundChatMessageReceived?.(incomingChatMessages[0])
          }
          return {
            ...prev,
            chat_messages: orderBy(
              unionBy(
                incomingChatMessages as ChatMessages[], prev?.chat_messages, 'id'
              ).filter(({whatsappMessagesStatus}) =>
                whatsappMessagesStatus !== WhatsappMessageStatusesEnum.Canceled
              ),
              'timestamp',
              'desc',
            ),
          }
        })
      },
      skip: !isPageVisible,
      variables,
    }
  )
  useEffect(
    () => {
      const lastReadChatMessage = lastChatMessageVisibleInTheViewPort
      if (
        lastReadChatMessage &&
        userId &&
        isPageVisible &&
        !isImpersonatedUser &&
        // Don't run mutation against optimistic chat message responses.
        // We use chat.type to discriminate optimistic responses from actual responses.
        // TODO: We should use a client side field for this instead.
        lastReadChatMessage.chat?.type &&
        (
          lastReadChatMessage.chat.type === 'INTERNAL' ||
          (
            // Do not run mutation for messages with null, SENDING or PENDING status.
            lastReadChatMessage.chat.type === 'EXTERNAL' &&
            lastReadChatMessage.whatsappMessagesStatus &&
            !([
              WhatsappMessageStatusesEnum.Sending,
              WhatsappMessageStatusesEnum.Pending,
            ] as string[]).includes(lastReadChatMessage.whatsappMessagesStatus)
          )
        )
      ) {
        // The mostRecentReadChatMessage can't be updated with a value older than
        // itself(the current one)
        if (
          mostRecentReadChatMessage && (
            new Date(lastReadChatMessage.insertionTimestamp as string).getTime() <=
            new Date(mostRecentReadChatMessage.insertionTimestamp as string).getTime()
          )
        ) {
          return
        }
        upsertMostRecentReadChatMessageId({
          variables: {chatId, mostRecentReadChatMessageId: lastReadChatMessage.id},
        })
      }
    },
    [
      chatId,
      chatMessages,
      isImpersonatedUser,
      isPageVisible,
      lastChatMessageVisibleInTheViewPort,
      mostRecentReadChatMessage,
      upsertMostRecentReadChatMessageId,
      userId,
    ]
  )
  useEffect(
    () => {
      if (isPageVisible && !firstLoadEffectRef.current) {
        // TODO: stop re-fetching here as it reinitialize the message lists when the user
        //  gets back to Flinkit. The user then looses the app state he left.
        //  This behavior is not user-friendly.
        refetchChatMessages().then(() => scrollToMostRecentChatMessage())
      }
    },
    [isPageVisible, refetchChatMessages, scrollToMostRecentChatMessage]
  )
  // Reset the load-ref for the first messages fetching once the chat was switched
  useEffect(() => {
    firstLoadEffectRef.current = true
  }, [chatId])
  // Actually applying the post-load effect for a new chat
  useEffect(() => {
    if (!firstLoadEffectRef.current || !chatMessages) return
    firstLoadEffectRef.current = false
    // Do not make the scroll to the bottom if the selected chat message is there
    // to avoid the scrollbar popping from the bottom to the selected message.
    if (chatMessageId) return
    setTimeout(scrollToMostRecentChatMessage, 50)
  }, [chatMessageId, chatMessages, scrollToMostRecentChatMessage])
  useEffect(() => {
    if (chatMessageInsertError) {
      notify(
        'chat.chatMessageBubble.whatsappMessageStatus.error.failedToSendWithoutErrorCode',
        {type: 'error'},
      )
    }
  }, [chatMessageInsertError, notify])
  useEffect(() => {
    // Refresh messages list when opening a different chat.
    refetchChatMessages()
  }, [chatId, refetchChatMessages])
  // Reset chat text and file attachment toolbar when opening a different chat
  useEffect(() => {
    if (!chatId) return
    setText("")
    setFileAttachments([])
    setCurrentlyRepliedChatMessage(undefined)
  }, [chatId, setFileAttachments, setCurrentlyRepliedChatMessage])
  // TODO: Remember and restore unsent messages per chat in local storage
  return (
    <Grid2 className={styles.chat} size={{xs: 12}} {...dropZoneProps}>
      {header}
      <Grid2
        className={`
          ${styles.chatMessagesContainer}
          ${isDragging ? styles.fileDragActive : ''}
        `}
        id="chatMessagesContainer"
        ref={chatMessageListContainerRef as MutableRefObject<HTMLDivElement>}
        size={{xs: 12}}
      >
        {(isLoadingChatMessages && !chatMessages) ? <LinearProgress /> : (
          <ChatMessageList
            chatMessages={chatMessages}
            fetchMoreChatMessages={fetchMoreChatMessages}
            isShowingTranslations={isShowingTranslations}
            onLastChatMessageVisibleInTheViewPortChange={
              setLastChatMessageVisibleInTheViewPort
            }
            onResendChatMessage={resendTextChatMessage}
            onOpenTemplateDialog={() => setIsTemplateDialogOpen(true)}
            ref={chatMessageListRef as any}
          />
        )}
      </Grid2>
      {visibleToolbar === 'CHAT_BULK_ACTIONS_TOOLBAR' && (
        <ChatBulkActionsToolbar />
      )}
      {visibleToolbar === 'CHAT_TOOLBAR' && (
        <ChatToolbar
          /* TODO: Any behavior requiring knowledge about the chat type should be
           *       implemented in the according chat page instead. It is bad practice to
           *       leak chat type specific implementation details here */
          buttons={(type === ChatTypesEnum.External) && (
            <>
              {/*
              TODO: Create template button which owns _entire_ template sending logic
              */}
              <IconButton
                color="primary"
                onClick={() => {
                  trackEvent({
                    action: 'OPEN_TEMPLATE_DIALOG',
                    category: 'CHAT',
                    source: 'CHAT_TOOLBAR',
                  })
                  setIsTemplateDialogOpen(true)
                }}
              >
                <ChatMessageTemplateToolbarIcon />
              </IconButton>
              <ChatAutoTranslationSettingButton
                chatId={chatId}
                isShowingTranslations={isShowingTranslations}
                onChangeIsShowingTranslations={onChangeIsShowingTranslations}
              />
            </>
          )}
          chatId={chatId}
          fileAttachments={fileAttachments}
          isInsertingChatMessage={isInsertingChatMessage || isUploadingFiles}
          isReplyWindowExpired={isReplyWindowExpired}
          isTemplateDialogOpen={isTemplateDialogOpen}
          onChangeTemplateDialogOpen={setIsTemplateDialogOpen}
          onChangeText={setText}
          onFilesButtonClick={isNativeMobileApp ?
            () => setIsMobileFilePickerPopperOpen(true) :
            openFileSelectionDialog
          }
          onSelectFileAttachments={setFileAttachments as any}
          onSendTemplateChatMessage={sendTemplateChatMessage}
          onSendTextChatMessage={sendTextChatMessage}
          text={text}
          onValidateFile={validateFile}
        />
      )}
      {fileAttachmentInput as ReactNode}
      {isNativeMobileApp && (
        <MobileFilePickerPopper
          anchorElementRef={chatMessageListContainerRef}
          isOpen={isMobileFilePickerPopperOpen}
          mimeTypes={mimeTypes.map(r => r.mimeType)}
          multiple
          onClose={() => setIsMobileFilePickerPopperOpen(false)}
          onSelectFiles={selectedFiles => {
            setFileAttachments(files => [...files, ...selectedFiles])
          }}
          onValidateFile={validateFile}
        />
      )}
      <Snackbar
        autoHideDuration={6000}
        message={useTranslate()('chat.toolbar.fileAttachment.draggedFileRejected')}
        open={isDraggedFileRejected}
      />
    </Grid2>
  )
}

const optimisticChatMessage = ({
  channelId = null, chatId, id, merchantUser, text, type,
  whatsappMessagesStatus = WhatsappMessageStatusesEnum.Sending,
}: optimisticChatMessageProps = {}) => ({
  insertChatMessage: {
    __typename: 'InsertChatMessageResult',
    chatMessage: {
      __typename: 'chat_messages',
      authorUser: {
        __typename: 'users',
        channelChatBotUser: null,
        customerUser: null,
        id: merchantUser?.user?.id ?? null,
        merchantApiUser: null,
        merchantUser: merchantUser ?? null,
      },
      authorUserDisplayName: '',
      // TODO: Set chat.type to proper value.
      //    We use chat.type to discriminate optimistic responses from actual responses.
      //    We should use a client side field for this instead.
      chat: {__typename: 'chat', channelId, id: chatId, isProductNews: false, type: null},
      chatId,
      // TODO: Include file attachments in optimistic response;
      //    this will require client-side rendering of attachment data.
      chatMessageFileAttachments: [],
      chatMessageTemplate: null,
      chatMessageTemplateValues: [],
      chatMessageTranslations: [],
      chatMessageWhatsappMessages: null,
      detectedLanguage: null,
      forwardedChatMessage: null,
      id,
      insertionTimestamp: (new Date()).toISOString(),
      lastReactionChatMessage: null,
      receivingErrorCode: null,
      repliedChatMessage: null,
      templateHeaderMediaKey: null,
      templateHeaderMediaMimeType: null,
      text,
      timestamp: (new Date()).toISOString(),
      type,
      whatsappMessagesStatus,
    },
  },
})

const useStyles = makeStyles(theme => ({
  chat: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    paddingLeft: 0,
    paddingRight: 0,
  },
  chatMessagesContainer: {
    backgroundColor: theme.palette.background.default,
    height: '100%',
    minWidth: '18.75rem',
    overflowAnchor: 'none',
    overflowX: 'hidden',
    overflowY: 'auto',
    paddingLeft: 0,
    paddingRight: 0,
  },
  fileDragActive: {
    filter: theme.palette.filters.dragAndDropGreen,
  },
  templateHeaderMediaImage: {
    borderRadius: '5px',
    height: '185px',
    objectFit: 'cover',
    width: '100%',
  },
}))

const CHAT_MESSAGES_LOAD_MORE_LIMIT = 25
const CUSTOMER_USER_FIELDS = `
  customerUser{
    id
    merchantCustomerUsers{companyName customerCode firstName id lastName}
    whatsappDisplayName
    whatsappPhoneNumber
  }
`
const PARTIAL_CHAT_MESSAGE_FIELDS = `
  authorUser{
    ${CUSTOMER_USER_FIELDS}
    channelChatBotUser{id}
    id
    merchantApiUser{id name}
    merchantUser{firstName id lastName}
  }
  authorUserDisplayName
  chatMessageFileAttachments{
    contentLanguage
    chatMessageFileAttachmentTranslations{language text}
    filename
    height
    id
    key
    metadata
    mimeType
    thumbnailMediaKey
    transcribedText
    width
  }
  chatMessageTemplate{
    buttonText
    buttonUrl
    chatMessageTemplateButtons{
      copyCodeExample
      id
      quickReply
      phoneNumber
      phoneNumberCountryCode
      phoneNumberText
      type
      url
      urlParameterExample
      urlText
      urlType
    }
    footer
    headline
    id
    text
    type
    whatsappMessageTemplates(where: {chatMessageTemplate: {type: {_eq: MAGIC}}}){
      id status
    }
  }
  chatMessageTemplateValues{
    id
    chatMessageTemplatePlaceholder{id index type}
    value
  }
  templateHeaderMediaKey
  templateHeaderMediaMimeType
  text
`
const CHAT_MESSAGE_FIELDS = `
  ${PARTIAL_CHAT_MESSAGE_FIELDS}
  __typename
  chatId
  chatMessageTranslations{language text}
  chatMessageWhatsappMessages{
    whatsappMessage{
      id
      lastSendAttemptErrorCode
      lastSendAttemptErrorDetails
      templateButtonCouponCode
      templateButtonUrlParameters
      type
      receivingErrorCode
      status
     }
  }
  chat{channelId id isProductNews type}
  detectedLanguage
  forwardedChatMessage{${PARTIAL_CHAT_MESSAGE_FIELDS}}
  id
  insertionTimestamp
  lastReactionChatMessage{id reactionEmoji}
  receivingErrorCode
  repliedChatMessage{
    authorUser{
      customerUser{
        id
        merchantCustomerUsers{firstName id lastName}
        whatsappDisplayName
      }
      id
    }
    chatId
    chatMessageFileAttachments{
      filename
      id
      key
      metadata
      mimeType
      thumbnailMediaKey
      transcribedText
    }
    chatMessageTemplate{id text}
    chatMessageTemplateValues{chatMessageTemplatePlaceholder{id index type} id value}
    id
    text
  }
  timestamp
  type
  whatsappMessagesStatus
`
const SELECTED_CHAT_MESSAGE_QUERY = gql`
  query($chatMessageId: uuid!){
    chat_messages_by_pk(id: $chatMessageId){id insertionTimestamp}
  }
`
const INCOMING_CHAT_MESSAGES_QUERY = gql`
  query($chatMessageIds: [uuid!]!){
    chat_messages(where: {id: {_in: $chatMessageIds}}){${CHAT_MESSAGE_FIELDS}}
  }
`
const CHAT_MESSAGES_QUERY = gql`
  query chat_messages(
    $chatId: uuid!
    $limit: Int!
    $cursorComparison: timestamptz_comparison_exp
    $timestampOrderBy: order_by
  ){
    chat_messages(
      limit: $limit
      order_by: {insertionTimestamp: $timestampOrderBy}
      where: {
        _or: [
          {chat: {type: {_eq: EXTERNAL}}, whatsappMessagesStatus: {_neq: CANCELED}}
          {chat: {type: {_eq: INTERNAL}}}
        ]
        chatId: {_eq: $chatId}
        insertionTimestamp: $cursorComparison
        type: {_neq: REACTION}
      }
    ){${CHAT_MESSAGE_FIELDS}}
  }
`
const CHAT_MESSAGE_EVENT_LOGS_SUBSCRIPTION = gql`
  subscription($chatId: uuid!, $cursor: timestamptz!, $limit: Int!){
    chat_messages_event_logs_stream(
      batch_size: $limit
      cursor: {initial_value: {insertionTimestamp: $cursor}, ordering: ASC}
      where: {
        chatMessage: {
          chatId: {_eq: $chatId}
          type: {_neq: REACTION}
        }
      }
    ){chatMessageId id}
  }
`
const GENERATE_PRESIGNED_URLS_MUTATION = gql`
  mutation GeneratePresignedUrls($count: Int!) {
    generatePresignedUrls(count: $count) {
      fields
      key
      url
    }
  }
`

const CHAT_MESSAGE_INSERT_MUTATION = gql`
  mutation(
    $chat: Chat!
    $chatMessageFileAttachmentIds: [String!]
    $chatMessageFileAttachments: [ChatMessageFileAttachment!]
    $chatMessageTemplateId: uuid
    $chatMessageTemplateValues: [ChatMessageTemplateValue!]
    $forwardedChatMessageId: uuid
    $id: uuid
    $platform: String!
    $repliedChatMessageId: uuid
    $templateHeaderMediaBase64Content: String
    $templateHeaderMediaMimeType: String
    $text: String
    $type: String!
  ) {
    insertChatMessage(
      chat: $chat
      chatMessageFileAttachmentIds: $chatMessageFileAttachmentIds
      chatMessageFileAttachments: $chatMessageFileAttachments
      chatMessageTemplateId: $chatMessageTemplateId
      chatMessageTemplateValues: $chatMessageTemplateValues
      forwardedChatMessageId: $forwardedChatMessageId
      id: $id
      platform: $platform
      repliedChatMessageId: $repliedChatMessageId
      templateHeaderMediaBase64Content: $templateHeaderMediaBase64Content
      templateHeaderMediaMimeType: $templateHeaderMediaMimeType
      text: $text
      type_: $type
    ) {chatMessage{${CHAT_MESSAGE_FIELDS}}}
  }
`
const USER_CHAT_FIELDS = `
  chatId
  mostRecentReadChatMessage{id insertionTimestamp}
  userId
`
const USER_CHAT_QUERY = gql`query($chatId: uuid!){
  user_chats(where: {chatId: {_eq: $chatId}}){${USER_CHAT_FIELDS}}
}`
const USER_CHAT_UPSERT_MUTATION = gql`
  mutation(
    $chatId: uuid!
    $mostRecentReadChatMessageId: uuid
  ){
    insert_user_chats_one(
      object: {
        chatId: $chatId
        mostRecentReadChatMessageId: $mostRecentReadChatMessageId
      }
      on_conflict: {
        constraint: user_chats_pkey
        update_columns: [mostRecentReadChatMessageId]
      }
    ){${USER_CHAT_FIELDS}}
  }
`

interface ChatProps {
  header: ReactNode,
  isReplyWindowExpired?: boolean,
  isShowingTranslations?: boolean,
  onChangeIsShowingTranslations?: (isShowingTranslations: boolean) => void,
  onExternalInboundChatMessageReceived?: (chatMessage: ChatMessages) => void
  type: ChatTypesEnum
}

interface optimisticChatMessageProps {
  channelId?: string|null
  chatId?: string
  id?: string
  merchantUser?: MerchantUsers
  text?: string|null
  type?: WhatsappMessageTypesEnum
  whatsappMessagesStatus?: WhatsappMessageStatusesEnum,
}

export default Chat
